import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Label } from "design-system/atoms/Label"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "design-system/molecules/Command"
import { ChevronDown } from "lucide-react"
import { useTranslation } from "react-i18next"

type Props = {
  label: string
  placeholder: string
  options: { value: string; label: string }[]
  selectedValues: string[]
  onChange: (values: string[]) => void
}

export function CatalogMultiSelect({
  label,
  placeholder,
  options,
  selectedValues,
  onChange,
}: Props) {
  const { t } = useTranslation()

  function toggle(value: string) {
    onChange(
      selectedValues.includes(value)
        ? selectedValues.filter((selected) => selected !== value)
        : [...selectedValues, value],
    )
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between h-10 px-3"
            disabled={options.length === 0}
          >
            <span className="truncate text-left font-normal">
              {selectedValues.length > 0
                ? t("capabilities.catalog.SearchCatalog.config.productSelection.selectedCount", {
                    count: selectedValues.length,
                  })
                : placeholder}
            </span>
            <ChevronDown className="h-5 w-5 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 min-w-56" align="start">
          <Command>
            <CommandInput
              placeholder={t("capabilities.catalog.SearchCatalog.config.productSelection.search")}
            />
            <CommandList>
              <CommandEmpty className="text-muted-foreground text-center pt-2 text-xs">
                {t("capabilities.catalog.SearchCatalog.config.productSelection.noResults")}
              </CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={`${option.value} ${option.label}`}
                    onSelect={() => toggle(option.value)}
                    className="gap-2"
                  >
                    <Checkbox checked={selectedValues.includes(option.value)} />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
